/**
 * Product Service
 * 
 * Handles all product and menu item API calls.
 */

import { apiService, ApiResponse } from './api.service';
import { API_ENDPOINTS } from '../config/api.config';
import type {
  Product,
  CreateProductRequest,
  UpdateProductRequest,
  /*ProductVariant,*/
  Category,
} from '../types/api.types';

class ProductService {
  /**
   * Get all products
   */
  async getAll(params?: any): Promise<ApiResponse<Product[]>> {
    const queryParams = params ? `?${new URLSearchParams(params)}` : '';
    return await apiService.get<Product[]>(`${API_ENDPOINTS.PRODUCTS.LIST}${queryParams}`);
  }

  /**
   * Get single product
   */
  async getById(id: string): Promise<ApiResponse<Product>> {
    return await apiService.get<Product>(API_ENDPOINTS.PRODUCTS.GET(id));
  }

  /**
   * Create product
   */
  async create(data: CreateProductRequest): Promise<ApiResponse<Product>> {
    return await apiService.post<Product>(API_ENDPOINTS.PRODUCTS.CREATE, data);
  }

  /**
   * Update product
   */
  async update(id: string, data: UpdateProductRequest): Promise<ApiResponse<Product>> {
    return await apiService.put<Product>(API_ENDPOINTS.PRODUCTS.UPDATE(id), data);
  }

  /**
   * Delete product
   */
  async delete(id: string): Promise<ApiResponse<void>> {
    return await apiService.delete<void>(API_ENDPOINTS.PRODUCTS.DELETE(id));
  }

  /**
   * Bulk update products
   */
  async bulkUpdate(updates: Array<{ id: string; data: UpdateProductRequest }>): Promise<ApiResponse<Product[]>> {
    return await apiService.post<Product[]>(API_ENDPOINTS.PRODUCTS.BULK_UPDATE, { updates });
  }

  /**
   * Bulk delete products
   */
  async bulkDelete(ids: string[]): Promise<ApiResponse<void>> {
    return await apiService.post<void>(API_ENDPOINTS.PRODUCTS.BULK_DELETE, { ids });
  }

  /**
   * Get products by category
   */
  async getByCategory(categoryId: string): Promise<ApiResponse<Product[]>> {
    return await apiService.get<Product[]>(API_ENDPOINTS.PRODUCTS.BY_CATEGORY(categoryId));
  }

  /**
   * Search products
   */
  async search(query: string): Promise<ApiResponse<Product[]>> {
    return await apiService.get<Product[]>(`${API_ENDPOINTS.PRODUCTS.SEARCH}?q=${encodeURIComponent(query)}`);
  }

  /**
   * Get popular products
   */
  async getPopular(limit: number = 10): Promise<ApiResponse<Product[]>> {
    return await apiService.get<Product[]>(`${API_ENDPOINTS.PRODUCTS.POPULAR}?limit=${limit}`);
  }

  /**
   * Get low stock products
   */
  async getLowStock(): Promise<ApiResponse<Product[]>> {
    return await apiService.get<Product[]>(API_ENDPOINTS.PRODUCTS.LOW_STOCK);
  }

  /**
   * Get out of stock products
   */
  async getOutOfStock(): Promise<ApiResponse<Product[]>> {
    return await apiService.get<Product[]>(API_ENDPOINTS.PRODUCTS.OUT_OF_STOCK);
  }

  /**
   * Toggle product availability
   */
  async toggleAvailability(id: string): Promise<ApiResponse<Product>> {
    return await apiService.post<Product>(API_ENDPOINTS.PRODUCTS.TOGGLE_AVAILABILITY(id));
  }

  /**
   * Upload product image
   */
  async uploadImage(id: string, image: File): Promise<ApiResponse<{ url: string }>> {
    return await apiService.upload<{ url: string }>(API_ENDPOINTS.PRODUCTS.UPLOAD_IMAGE(id), image, 'image');
  }

  /**
   * Delete product image
   */
  async deleteImage(id: string, imageId: string): Promise<ApiResponse<void>> {
    return await apiService.delete<void>(API_ENDPOINTS.PRODUCTS.DELETE_IMAGE(id, imageId));
  }

  /**
   * Get product variants
   */
  // async getVariants(id: string): Promise<ApiResponse<ProductVariant[]>> {
  //   return await apiService.get<ProductVariant[]>(API_ENDPOINTS.PRODUCTS.VARIANTS(id));
  // }

  // /**
  //  * Add product variant
  //  */
  // async addVariant(id: string, variant: Omit<ProductVariant, 'id'>): Promise<ApiResponse<ProductVariant>> {
  //   return await apiService.post<ProductVariant>(API_ENDPOINTS.PRODUCTS.ADD_VARIANT(id), variant);
  // }

  // /**
  //  * Update product variant
  //  */
  // async updateVariant(id: string, variantId: string, data: Partial<ProductVariant>): Promise<ApiResponse<ProductVariant>> {
  //   return await apiService.put<ProductVariant>(API_ENDPOINTS.PRODUCTS.UPDATE_VARIANT(id, variantId), data);
  // }

  /**
   * Delete product variant
   */
  async deleteVariant(id: string, variantId: string): Promise<ApiResponse<void>> {
    return await apiService.delete<void>(API_ENDPOINTS.PRODUCTS.DELETE_VARIANT(id, variantId));
  }

  // ============================================
  // Category Methods
  // ============================================

  /**
   * Get all categories
   */
  async getAllCategories(): Promise<ApiResponse<Category[]>> {
    return await apiService.get<Category[]>(API_ENDPOINTS.CATEGORIES.LIST);
  }

  /**
   * Get single category
   */
  async getCategoryById(id: string): Promise<ApiResponse<Category>> {
    return await apiService.get<Category>(API_ENDPOINTS.CATEGORIES.GET(id));
  }

  /**
   * Create category
   */
  async createCategory(data: Partial<Category>): Promise<ApiResponse<Category>> {
    return await apiService.post<Category>(API_ENDPOINTS.CATEGORIES.CREATE, data);
  }

  /**
   * Update category
   */
  async updateCategory(id: string, data: Partial<Category>): Promise<ApiResponse<Category>> {
    return await apiService.put<Category>(API_ENDPOINTS.CATEGORIES.UPDATE(id), data);
  }

  /**
   * Delete category
   */
  async deleteCategory(id: string): Promise<ApiResponse<void>> {
    return await apiService.delete<void>(API_ENDPOINTS.CATEGORIES.DELETE(id));
  }

  /**
   * Reorder categories
   */
  async reorderCategories(order: Array<{ id: string; sortOrder: number }>): Promise<ApiResponse<Category[]>> {
    return await apiService.post<Category[]>(API_ENDPOINTS.CATEGORIES.REORDER, { order });
  }

  /**
   * Toggle category active status
   */
  async toggleCategoryActive(id: string): Promise<ApiResponse<Category>> {
    return await apiService.post<Category>(API_ENDPOINTS.CATEGORIES.TOGGLE_ACTIVE(id));
  }

  /**
   * Get category statistics
   */
  async getCategoryStats(id: string): Promise<ApiResponse<any>> {
    return await apiService.get(API_ENDPOINTS.CATEGORIES.STATS(id));
  }
}

export const productService = new ProductService();
